import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiRefreshCw } from "react-icons/fi";
import { fetchCrmDashboard } from "../crmApi";
import { useCrmCopy } from "../crmI18n";
import { buildCrmMetricItems, CRM_PIPELINE_ICON } from "../crmNavigation";

const defaultDashboard = {
  metrics: {},
  pipeline: [],
  upcoming_tasks: [],
  recent_activity: [],
};

function getContactLabel(contact) {
  if (!contact) return "";
  const fullName = contact?.full_name || [contact?.first_name, contact?.last_name].filter(Boolean).join(" ");
  return fullName || contact?.email || contact?.phone || "";
}

function getTaskRelatedLabel(task, copy) {
  if (task?.lead?.title) return task.lead.title;
  if (task?.conversation?.subject) return task.conversation.subject;
  if (task?.company?.name) return task.company.name;
  return getContactLabel(task?.contact) || copy.common.noRecords;
}

function getTaskOwnerLabel(task) {
  const owner = task?.owner;
  if (!owner) return "";
  return owner.name || owner.email || (owner.id ? `User ${owner.id}` : "");
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

export default function Dashboard() {
  const copy = useCrmCopy();
  const [dashboard, setDashboard] = useState(defaultDashboard);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchCrmDashboard();
      setDashboard({
        ...defaultDashboard,
        ...(data || {}),
      });
    } catch (err) {
      console.error("[CRM] Dashboard load error:", err);
      setError(err?.response?.data?.error || copy.dashboard.error);
    } finally {
      setLoading(false);
    }
  }, [copy.dashboard.error]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const metricItems = useMemo(() => buildCrmMetricItems(copy), [copy]);

  const totalPipelineValue = useMemo(
    () =>
      (dashboard.pipeline || []).reduce((sum, row) => {
        const value = Number(row?.value || 0);
        return Number.isFinite(value) ? sum + value : sum;
      }, 0),
    [dashboard.pipeline]
  );

  const PipelineIcon = CRM_PIPELINE_ICON;
  const canViewAllTaskAgenda = Boolean(dashboard.permissions?.can_view_all_tasks);

  return (
    <main className="crm-page crm-page--compactTable crm-page--dashboard">
      <header className="crm-pageHeader">
        <div>
          <p className="crm-eyebrow">CRM</p>
          <h1>{copy.dashboard.title}</h1>
        </div>

        <button type="button" className="crm-ghostButton" onClick={loadDashboard} disabled={loading}>
          <FiRefreshCw aria-hidden="true" />
          <span>{loading ? copy.dashboard.loading : copy.dashboard.refresh}</span>
        </button>
      </header>

      {error && <div className="crm-stateBanner is-error">{error}</div>}

      <section className="crm-metricGrid" aria-label={copy.dashboard.metricsAria}>
        {metricItems.map((metric) => (
          <article key={metric.key} className="crm-metricCard">
            <span>{metric.label}</span>
            <strong>{loading ? "-" : dashboard.metrics?.[metric.key] ?? 0}</strong>
          </article>
        ))}
      </section>

      <section className="crm-dashboardGrid">
        <article className="crm-panel crm-panel--pipeline">
          <div className="crm-panelHeader">
            <div>
              <p className="crm-panelKicker">{copy.dashboard.pipelineKicker}</p>
              <h2>{copy.dashboard.opportunitiesTitle}</h2>
            </div>
            <div className="crm-panelIcon">
              <PipelineIcon aria-hidden="true" />
            </div>
          </div>

          <div className="crm-pipelineList">
            {(dashboard.pipeline || []).map((stage) => (
              <div key={stage.key || stage.label} className="crm-pipelineRow">
                <div>
                  <strong>{copy.leadStages?.[stage.key] || stage.label}</strong>
                  <span>{copy.dashboard.openCount(stage.count || 0)}</span>
                </div>
                <span>{stage.value || 0}</span>
              </div>
            ))}
          </div>

          <div className="crm-panelFooter">
            <span>{copy.dashboard.totalValue}</span>
            <strong>{totalPipelineValue}</strong>
          </div>
        </article>

        <article className="crm-panel crm-panel--agenda">
          <div className="crm-panelHeader">
            <div>
              <p className="crm-panelKicker">{copy.dashboard.agendaKicker}</p>
              <h2>{copy.dashboard.tasksTitle}</h2>
            </div>
          </div>
          {dashboard.upcoming_tasks?.length ? (
            <div className="crm-compactList">
              {dashboard.upcoming_tasks.map((task) => (
                <article key={task.id} className="crm-compactListRow">
                  <div>
                    <strong>{task.title}</strong>
                    <span>{getTaskRelatedLabel(task, copy)}</span>
                    {canViewAllTaskAgenda && getTaskOwnerLabel(task) ? (
                      <span>{`Owner: ${getTaskOwnerLabel(task)}`}</span>
                    ) : null}
                  </div>
                  <div className="crm-compactListMeta">
                    <span>{task.due_at ? formatDateTime(task.due_at) : copy.dashboard.dueAt}</span>
                    <span className={`crm-statusPill crm-statusPill--${task.priority || "normal"}`}>
                      {copy.taskPriorities?.[task.priority] || task.priority}
                    </span>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <EmptyList label={copy.dashboard.emptyTasks} />
          )}
        </article>

        <article className="crm-panel crm-panelWide crm-panel--timeline">
          <div className="crm-panelHeader">
            <div>
              <p className="crm-panelKicker">{copy.dashboard.timelineKicker}</p>
              <h2>{copy.dashboard.recentActivityTitle}</h2>
            </div>
          </div>
          {dashboard.recent_activity?.length ? (
            <div className="crm-compactList">
              {dashboard.recent_activity.map((activity) => (
                <article key={activity.id} className="crm-compactListRow">
                  <div>
                    <strong>{activity.subject}</strong>
                    <span>
                      {copy.conversationChannels?.[activity.channel] || activity.channel}
                      {" · "}
                      {copy.conversationDirections?.[activity.direction] || activity.direction}
                    </span>
                  </div>
                  <div className="crm-compactListMeta">
                    <span>{formatDateTime(activity.occurred_at)}</span>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <EmptyList label={copy.dashboard.emptyActivity} />
          )}
        </article>
      </section>
    </main>
  );
}

function EmptyList({ label }) {
  return (
    <div className="crm-emptyList">
      <span>{label}</span>
    </div>
  );
}
